import { switchMap, takeUntil, lastValueFrom } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { RosbagBuffer } from './rosbag-buffer';

import { BlobReadable } from '@mcap/browser';
import { McapIndexedReader } from '@mcap/core';
import { parse } from '@foxglove/rosmsg';
import { MessageReader } from '@foxglove/rosmsg2-serialization';
import { ZSTDDecoder } from 'zstddec';
import { scanIncorrectBoxReleaseMsgOrder } from './rosbag-reader-utils';

function nsToTime(nanoseconds: bigint) {
  const nsNum = Number(nanoseconds);
  const seconds = Math.floor(nsNum / 1e9);
  const nanosecondsRemainder = nsNum - seconds * 1e9;
  return {
    sec: seconds,
    nsec: nanosecondsRemainder,
  };
}

export function readROS2Bag(path: string, buffer: RosbagBuffer): Promise<void> {
  return lastValueFrom(
    fromFetch(path).pipe(
      // Only read until the bag is destroyed (insurance).
      takeUntil(buffer.destroy$),
      switchMap((res) => res.blob()), // Grab the blob from the response
      switchMap((blob) => {
        const decoder = new ZSTDDecoder();

        return decoder.init().then(() => {
          const decompressHandlers = {
            zstd: (buffer: Uint8Array, decompressedSize: bigint) =>
              decoder.decode(buffer, Number(decompressedSize)),
          };

          return McapIndexedReader.Initialize({
            readable: new BlobReadable(blob),
            decompressHandlers,
          });
        });
      }),
      switchMap(async (reader) => {
        // Extract start and end time from
        buffer.info.startTime = nsToTime(reader.statistics.messageStartTime);
        buffer.info.endTime = nsToTime(reader.statistics.messageEndTime);

        // Update total message count
        buffer.info.totalNrMsgs = Number(reader.statistics.messageCount);

        // Grab all topics in bag.
        reader.channelsById.forEach((channel) => {
          buffer.info.topics.push({
            name: channel.topic,
            messageType: channel.type,
          });
        });

        const messageReaderByChannelId = new Map<number, MessageReader>();

        // For each channel (topic) we read its schema and create a message reader that
        // is able to parse the content of each message.
        for (const channel of reader.channelsById.values()) {
          if (channel.messageEncoding !== 'cdr') {
            throw new Error(
              `Encoding '${channel.messageEncoding}' not supported. Only supports 'cdr' encoding`
            );
          }

          if (
            channel.schemaId !== 0 &&
            !reader.schemasById.has(channel.schemaId)
          ) {
            throw new Error(`Missing schema for ${channel.topic}}`);
          }

          const schema = reader.schemasById.get(channel.schemaId);
          const messageReader = new MessageReader(
            parse(new TextDecoder().decode(schema.data), {
              ros2: true,
            })
          );

          messageReaderByChannelId.set(channel.id, messageReader);
        }

        // Read messages
        buffer.readProgress$.next(0);
        for await (const msg of reader.readMessages()) {
          const message = messageReaderByChannelId
            .get(msg.channelId)
            .readMessage(msg.data);

          const wrappedMessage = {
            timestamp: nsToTime(msg.publishTime),
            topic: reader.channelsById.get(msg.channelId).topic,
            message,
          };

          buffer.addMessage(wrappedMessage);
          buffer.readProgress$.next(
            (buffer.getNrBufferedMsgs() / buffer.getTotalNrMsgs()) * 100
          );
        }

        scanIncorrectBoxReleaseMsgOrder(buffer);
      })
    )
  );
}
